import { useA11yMessage } from "containers/A11yStatusProvider/A11yStatusContext";
import {
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { QueryProgress } from "core/types";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "translations";
import { useMedia } from "../ResponsiveMedia";

export function useCopy({
  copySuccessMessage,
  onCopyMessage,
  onFailure,
  onSuccess,
  resetDelay = 1000,
}: {
  copySuccessMessage?: string;
  onCopyMessage: string;
  onFailure?: () => void;
  onSuccess?: () => void;
  resetDelay?: number;
}) {
  const translations = useTranslations();
  const { isDesktop } = useMedia();
  const [copyStatus, setCopyStatus] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const { updateA11yMessage } = useA11yMessage();
  const [copyMessage, setCopyMessage] = useState<string>(onCopyMessage);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (copyStatus === QUERY_PROGRESS_SUCCEED) {
      const msg =
        copySuccessMessage ??
        translations.insuranceApp.eligibilityTab.approvalFlow.tooltipLinkCopied;
      updateA11yMessage(msg);
      setCopyMessage(msg);
    } else if (copyStatus === QUERY_PROGRESS_FAILED) {
      const msg =
        translations.providersearch.providerDetailsPage.contactSection
          .copyEmailFailedTooltip;
      updateA11yMessage(msg);
      setCopyMessage(msg);
    } else {
      setCopyMessage(onCopyMessage);
    }
  }, [
    copyStatus,
    onCopyMessage,
    copySuccessMessage,
    translations,
    updateA11yMessage,
    isDesktop,
  ]);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  const copyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus(QUERY_PROGRESS_SUCCEED);
      onSuccess?.();
    } catch (err) {
      console.error("Error copying to clipboard", err);
      setCopyStatus(QUERY_PROGRESS_FAILED);
      onFailure?.();
    } finally {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => {
        setCopyStatus(QUERY_PROGRESS_NOT_STARTED);
      }, resetDelay);
    }
  };

  return { copyMessage, copyStatus, copyToClipboard };
}
